<template>
  <transition name="modal">
    <div
      v-if="toggleModal"
      class="modal-mask post-modal"
    >
      <div class="modal-wrapper" @click.self="closeModal">
        <div
          ref="modalRoot"
          class="modal-container modal-container--fit post-modal-content"
          :class="[hasMedia, post.network, hasComment, post.post_type, noAvatar]"
        >
          <button
            type="button"
            class="post-modal-close"
            title="Close modal"
            data-test="producer-postModal-close"
            @click="closeModal"
          >
            ×
          </button>

          <div class="post-content">
            <div v-if="hasMedia" :data-live-video="isLiveVideo" class="media-module">
              <template v-if="post.post_type === 'video'">
                <template v-if="post.network === 'youtube'">
                  <iframe
                    id="youtube-player"
                    class="embed-responsive-item"
                    :src="youtubeSrcUrl"
                    frameborder="0"
                    allowfullscreen=""
                    data-test="producer-postModal-youtubeVideo"
                  ></iframe>
                </template>

                <template v-else-if="post.network === 'facebook'">
                  <div class="fb-video-placeholder" data-test="producer-postModal-facebookVideo"></div>
                </template>

                <template v-else>
                  <video
                    alt=""
                    autoplay="autoplay"
                    controls="controls"
                    class="media video"
                    type="video/mp4"
                    muted
                    :loop="post.videos[0].type === 'gif'"
                    :poster="post.videos[0].p"
                    :src="post.videos[0].m"
                    :data-test="`producer-postModal-${post.network}Video`"
                    @error="videoError"
                    @volumechange="previewVideoVolumeChange($event.target.muted ? 0 : $event.target.volume)"
                    @loadeddata="onVideoStarted"
                  ></video>
                </template>
              </template>

              <template v-else>
                <img
                  class="media photo"
                  :src="photoUrl"
                  alt=""
                  data-test="producer-postModal-image"
                  @error="imageError"
                >
              </template>
            </div>

            <div class="text-module">
              <div :class="verified" class="meta-author clearfix">
                <a v-if="hasAvatar" class="avatar" :href="getUserUrl" target="_blank">
                  <img
                    :src="post.user_profile_image_url"
                    alt=""
                    class="user-img"
                    data-test="producer-postModal-avatar"
                  >
                </a>

                <template v-if="post.network === 'instagram'">
                  <a
                    v-if="post.user_name"
                    class="username"
                    :href="getUserUrl"
                    target="_blank"
                    data-test="producer-postModal-userUrl"
                  >
                    <span class="handle" data-test="producer-postModal-username">
                    @<span v-html="post.user_name"></span>
                    </span>
                  </a>

                  <a
                    v-else
                    :href="post.permalink"
                    class="username"
                    target="_blank"
                    data-test="producer-postModal-userUrl"
                  >
                    <span class="handle" data-test="producer-postModal-username">Instagram user</span>
                  </a>
                </template>

                <template v-if="post.network === 'share-it'">
                  <span class="handle"><span v-html="post.user_name"></span></span>
                </template>

                <template v-if="post.network === 'reddit'">
                  <a
                    v-if="post.service_meta.subreddit"
                    :href="post.permalink"
                    class="username"
                    target="_blank"
                    data-test="producer-postModal-subredditUrl"
                  >
                    in r/<span v-html="post.service_meta.subreddit"></span>
                  </a>

                  <a
                    :href="getUserUrl"
                    class="username"
                    target="_blank"
                    data-test="producer-postModal-userUrl"
                  >
                    <span class="handle" data-test="producer-postModal-username">by u/<span v-html="post.user_real_name"></span></span>
                  </a>
                </template>

                <template v-if="post.network !== 'instagram' && post.network !== 'reddit'">
                  <a
                    :href="getUserUrl"
                    class="username"
                    target="_blank"
                    data-test="producer-postModal-userUrl"
                  >
                    <span class="author" v-html="post.user_real_name" data-test="producer-postModal-author"></span>
                    <span
                      v-if="post.network === 'twitter' || post.network === 'tiktok' || post.network === 'threads'"
                      class="handle"
                      data-test="producer-postModal-username"
                    >
                      @<span v-html="post.user_name"></span>
                    </span>
                    <span v-else-if="isChat" class="handle">
                      <span v-html="post.user_name" data-test="producer-postModal-username"></span>
                    </span>
                  </a>
                </template>
              </div>

              <div
                v-if="post.network === 'tiktok'"
                class="audio-attribution"
                :title="post.service_meta.music_name + ' - ' + post.service_meta.music_author"
                data-test="producer-postModal-audioAttribution"
              >
                <span class="audio-icon fas fa-music"></span>
                <span class="audio-info">{{ post.service_meta.music_name }} - {{ post.service_meta.music_author }}</span>
              </div>

              <div v-if="isComment" class="meta-comment">
                <template v-if="post.network === 'reddit'">
                  Comment posted in <strong class="post-title" v-html="post.title" data-test="producer-postModal-subredditSource"></strong>
                  <br>
                  on
                  <a
                    :href="post.permalink"
                    class="timestamp"
                    target="_blank"
                    data-test="producer-postModal-timestamp"
                  >
                    {{ timestampToString(post.post_time) }}
                  </a>
                </template>
                <template v-else>
                  Comment posted on
                  <a
                    :href="post.permalink"
                    class="timestamp"
                    target="_blank"
                    data-test="producer-postModal-timestamp"
                  >
                    {{ timestampToString(post.post_time) }}
                  </a>
                </template>
              </div>

              <div v-else class="post-time">
                <a
                  :href="post.permalink"
                  class="timestamp"
                  target="_blank"
                  data-test="producer-postModal-timestamp"
                >
                  {{ timestampToString(post.post_time) }}
                </a>
              </div>

              <div class="message">
                <div
                  v-if="!isComment && post.title"
                  class="post-title"
                  v-html="post.title"
                  data-test="producer-postModal-title"
                ></div>
                <div class="post-text" v-html="postHtml" data-test="producer-postModal-text"></div>
              </div>

              <div
                class="origin-flag"
                data-test="producer-postModal-networkIcon"
              >
                <i
                  :class="networkIconClass"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import PostUtils from '@tagboard/helpers/post-utils';
import MediaProxy from '@tagboard/helpers/media-proxy';
import { fetch } from '../utils/request';
import { timestampToString } from '../utils/filters';

function hashtagUrl(network, hashtag) {
  switch (network) {
    case 'tiktok':
      return `https://tiktok.com/tag/${hashtag}`;
    case 'facebook':
      return `https://www.facebook.com/hashtag/${hashtag}`;
    case 'instagram':
      return `https://www.instagram.com/explore/tags/${hashtag}/`;
    case 'youtube':
      return `https://www.youtube.com/results?search_query=%23${hashtag}`;
    case 'threads':
      return `https://threads.com/search?q=${hashtag}`;
    default:
      return `https://twitter.com/search?q=%23${hashtag}`;
  }
}

export default {
  data() {
    return {
      proxyAttempt: false,
      errored: false,
    };
  },

  mounted() {
    this.cleanText();
  },

  updated() {
    this.cleanText();
  },

  methods: {
    timestampToString,

    closeModal() {
      this.$store.dispatch('hideModal');
      this.$store.dispatch('clearModalPost');
    },

    cleanText() {
      const root = this.$refs.modalRoot;
      if (!root?.querySelectorAll) {
        return;
      }

      const links = root.querySelectorAll('a.hashtag');

      links.forEach((el) => {
        const href = el.getAttribute('href');

        if (href?.[0] === '/') {
          el.setAttribute('href', hashtagUrl(this.post.network, href.substring(1)));
        }
      });
    },

    imageError() {
      if (!this.proxyAttempt && MediaProxy.usingNetwork(this.post.network)) {
        this.proxyAttempt = true;
      }
    },

    videoError() {
      if (this.errored) {
        return;
      }

      this.errored = true;

      let endpoint;

      switch (this.post.network) {
        case 'instagram': {
          let shortcode = this.post.permalink.replace(/\/$/, '').split('/');
          shortcode = shortcode[shortcode.length - 1];
          endpoint = `${process.env.INSTAGRAM_API}/video/${shortcode}?post_id=${this.post.post_id}`;
          break;
        }

        case 'tiktok': {
          endpoint = `${process.env.TIKTOK_API}/video/${this.post.post_id}`;
          break;
        }

        default:
          return;
      }

      fetch(endpoint).then((url) => {
        this.$refs.modalRoot?.querySelector('video')?.setAttribute('src', url);
      });
    },

    previewVideoVolumeChange(volume) {
      this.$store.commit('SET_PREVIEW_VIDEO_VOLUME', volume);
    },

    onVideoStarted(e) {
      // eslint-disable-next-line no-param-reassign
      e.target.volume = this.$store.state.user.previewVideoVolume;
    },
  },

  watch: {
    post() {
      this.errored = false;
    },
  },

  computed: {
    post() {
      return this.$store.state.posts.postModalPost || {};
    },

    hasMedia() {
      if (!this.post) {
        return '';
      }

      const hasPhotos = this.post.photos && this.post.photos.length;
      const hasVideos = this.post.videos && this.post.videos.length;
      if (hasPhotos || hasVideos) {
        return 'has-media';
      }

      return '';
    },

    isChat() {
      return this.post.post_type === 'chat';
    },

    isComment() {
      if (this.post.tgb_meta && this.post.tgb_meta.isComment) {
        return true;
      }

      return false;
    },

    hasComment() {
      if (this.post.tgb_meta && this.post.tgb_meta.isComment) {
        return 'has-comment';
      }

      return '';
    },

    networkClass() {
      if (!this.post.network) {
        return '';
      }

      return `${this.post.network}-modal`;
    },

    networkIconClass() {
      if (!this.post.network) {
        return '';
      }

      if (this.post.network === 'instagram-stories') {
        return 'fab fa-instagram';
      }

      if (this.post.network === 'twitter') {
        return 'fab fa-x-twitter';
      }

      const networkIcons = {
        facebook: 'fab fa-facebook-f',
        instagram: 'fab fa-instagram',
        tiktok: 'fab fa-tiktok',
        twitch: 'fab fa-twitch',
        youtube: 'fab fa-youtube',
        threads: 'fab fa-threads',
        vimeo: 'fab fa-vimeo',
        x: 'fab fa-x-twitter',
      };

      return networkIcons[this.post.network] || '';
    },

    verified() {
      const verified = !!(this.post.service_meta && this.post.service_meta.user_verified);
      return {
        verified,
        gov: verified && this.post.service_meta.verified_type === 'government',
        org: verified && this.post.service_meta.verified_type === 'business',
      };
    },

    hasAvatar() {
      const noAvatarNetworks = ['instagram', 'zipwhip', 'twilio', 'reddit', 'share-it'];
      if (noAvatarNetworks.indexOf(this.post.network) !== -1) {
        return false;
      }

      return true;
    },

    noAvatar() {
      let hasAvatar = true;

      switch (this.post.network) {
        case 'instagram':
        case 'reddit':
        case 'share-it':
          hasAvatar = false;
          break;
        case 'facebook':
          if (this.post.tgb_meta && this.post.tgb_meta.isComment) {
            hasAvatar = false;
          }

          break;
        default:
          break;
      }

      return hasAvatar ? '' : 'no-avatar';
    },

    youtubeSrcUrl() {
      let url = `https://www.youtube.com/embed/${this.post.post_id}`;
      url += `?enablejsapi=1&amp;origin=${this.base_url}&amp;rel=0&amp;controls=2&amp;showinfo=0`;
      return url;
    },

    getUserUrl() {
      let url;
      switch (this.post.network) {
        case 'twitter':
          url = `https://twitter.com/${this.post.user_name}`;
          break;
        case 'instagram':
          url = `https://instagram.com/${this.post.user_name}`;
          break;
        case 'facebook':
          url = `https://facebook.com/${this.post.user_id}`;
          break;
        case 'youtube':
          if (this.post.user_name) {
            url = `https://youtube.com/user/${this.post.user_name}`;
          } else {
            url = `https://youtube.com/channel/${this.post.user_id}`;
          }

          break;
        case 'reddit':
          url = `https://reddit.com/user/${this.post.user_real_name}`;
          break;
        case 'tiktok':
          url = `https://tiktok.com/@${this.post.user_name}`;
          break;
        case 'threads':
          url = `https://threads.com/@${this.post.user_name}`;
          break;
        default:
          break;
      }

      return url;
    },

    photoUrl() {
      if (this.post.post_type === 'photo' && this.post.photos?.length) {
        let url = this.post.photos[0].m;
        if (this.post.network === 'facebook') {
          url = this.post.photos[0].l || this.post.photos[0].m;
        } else if (this.post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(this.post, 'l');
        }

        return this.proxyAttempt ? MediaProxy.genUrl(url) : url;
      }

      if (this.post.videos?.length) {
        let url = this.post.videos[0].p;
        if (this.post.network === 'youtube') {
          url = this.post.videos[0].t;
        } else if (this.post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(this.post, 'l');
        }

        return this.proxyAttempt ? MediaProxy.genUrl(url) : url;
      }

      return '';
    },

    toggleModal() {
      return this.$store.state.posts.postModalShown;
    },

    postTemplate() {
      if (this.$store.state.posts.postModalPost) {
        if (this.$store.state.posts.postModalPost === 'promo') {
          return 'promo';
        }

        if (this.$store.state.posts.postModalPost === 'settings') {
          return 'settings';
        }

        return this.$store.state.posts.postModalPost.network;
      }

      return '';
    },

    isLiveVideo() {
      if (this.post.service_meta && this.post.service_meta.published_live) {
        return this.post.videos[0].m;
      }

      return '';
    },

    postHtml() {
      return this.post.html || this.post.text;
    },
  },
};
</script>
